import { useParams, Link } from "react-router-dom";
import { ArrowLeft, MessageCircle, Facebook, Share2 } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { blogPosts } from "@/data/blogPosts";
import { CONTACT } from "@/i18n/translations";
import Reveal from "@/components/Reveal";
import AzulejoStrip from "@/components/AzulejoStrip";
import Seo from "@/components/Seo";

const BlogPost = () => {
  const { slug } = useParams();
  const { lang } = useLanguage();
  const post = blogPosts.find((p) => p.slug === slug);

  if (!post) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center" data-testid="blog-not-found">
        <p className="font-serif text-4xl text-ink mb-6">404</p>
        <Link to="/blog" className="text-bronze underline" data-testid="blog-not-found-link">
          {lang === "es" ? "Volver al blog" : "Back to the blog"}
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="blog-post-page">
      <Seo
        title={`${post.title[lang]} | Marina Knows`}
        description={post.excerpt[lang]}
        image={`https://marinaknows.com/images/blog/${post.slug}.jpg`}
      />

      <article className="max-w-3xl mx-auto px-6 md:px-0 pt-16 md:pt-24 pb-16">
        <Reveal>
          <Link to="/blog" data-testid="blog-back-link" className="inline-flex items-center gap-2 text-sm text-stone-fg hover:text-bronze transition-colors mb-10">
            <ArrowLeft size={16} aria-hidden />
            {lang === "es" ? "Todas las guías" : "All guides"}
          </Link>
          <div className="flex items-center gap-4 mb-6">
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-azul" data-testid="post-tag">{post.tag[lang]}</span>
            <span className="font-mono text-xs text-stone-fg">{post.readTime[lang]}</span>
          </div>
          <h1 className="font-serif text-4xl md:text-5xl tracking-tight leading-[1.15] text-ink mb-10" data-testid="post-title">
            {post.title[lang]}
          </h1>
          <div className="overflow-hidden rounded-2xl border border-stone mb-12">
            <img
              src={`/images/blog/${post.slug}.jpg`}
              alt={post.title[lang]}
              className="w-full object-cover"
              data-testid="post-cover"
            />
          </div>
        </Reveal>

        <div className="space-y-12">
          {post.sections.map((section, i) => (
            <Reveal key={i} delay={i * 0.03}>
              <section data-testid={`post-section-${i}`}>
                <h2 className="font-serif text-2xl md:text-3xl text-ink mb-4">{section.h[lang]}</h2>
                {section.p.map((para, j) => (
                  <p key={j} className="text-lg leading-relaxed text-ink/75 mb-4">{para[lang]}</p>
                ))}
              </section>
            </Reveal>
          ))}
        </div>

        <Reveal>
          <div className="mt-14 flex items-center gap-4 border-t border-stone pt-8" data-testid="post-share">
            <span className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-stone-fg">
              <Share2 size={14} aria-hidden />
              {lang === "es" ? "Compartir" : "Share"}
            </span>
            <a
              href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(`https://marinaknows.com/blog/${post.slug}`)}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="share-facebook"
              aria-label="Share on Facebook"
              className="p-3 rounded-full border border-stone text-azul hover:border-azul hover:bg-azul-soft/50 transition-all hover:-translate-y-0.5"
            >
              <Facebook size={18} aria-hidden />
            </a>
            <a
              href={`https://wa.me/?text=${encodeURIComponent(`${post.title[lang]} — https://marinaknows.com/blog/${post.slug}`)}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="share-whatsapp"
              aria-label="Share on WhatsApp"
              className="p-3 rounded-full border border-stone text-citrus hover:border-citrus hover:bg-citrus-soft/50 transition-all hover:-translate-y-0.5"
            >
              <MessageCircle size={18} aria-hidden />
            </a>
          </div>
        </Reveal>

        <Reveal>
          <div className="mt-10 bg-terracotta-soft/50 border border-terracotta/40 rounded-2xl p-8 md:p-10 text-center" data-testid="post-cta">
            <p className="font-serif text-2xl md:text-3xl text-ink mb-4">
              {lang === "es" ? "¿Quieres que lo gestione yo por ti?" : "Want me to handle it for you?"}
            </p>
            <p className="text-ink/70 mb-7 max-w-md mx-auto">
              {lang === "es"
                ? "Precio fijo acordado antes de empezar. Un mensaje y nos ponemos con ello."
                : "Fixed price agreed before we start. One message and we're on it."}
            </p>
            <a
              href={CONTACT.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="post-cta-whatsapp"
              className="inline-flex items-center gap-2 bg-terracotta hover:bg-terracotta-dark text-ink font-medium px-7 py-3.5 rounded-full transition-all hover:-translate-y-0.5"
            >
              <MessageCircle size={17} aria-hidden />
              {lang === "es" ? "Escríbeme por WhatsApp" : "Message me on WhatsApp"}
            </a>
          </div>
        </Reveal>
      </article>

      <AzulejoStrip testid="post-azulejo" />
    </div>
  );
};

export default BlogPost;
